// Поле карточки: подпись, значение и, если нужно, пояснение под значением.
//
// Правило 7 держится типом: значение — либо строка, либо ЯВНОЕ «данных нет». Прочерк без
// объяснения не даёт различить «не заполнено» и «ноль по расчёту», и человек идёт спрашивать
// в чат то, что должно быть написано на экране.

import type { ReactNode } from 'react';

import { Skeleton } from '@/shared/ui/screen-state';

export interface FieldRowProps {
  label: string;
  /** Значение. null — данных нет, тогда обязателен `missing`: кто и что должен заполнить. */
  value: ReactNode | null;
  /**
   * Что показать вместо значения, когда его нет. КОРОТКО: «не заполнена», «не присвоен».
   *
   * Именно коротко, а не фразой. До 04.09.2026 сюда клали причину вместе со следствием одной
   * строкой — «не заполнена: её вносят кадры при оформлении», — и на карточке с тремя
   * незаполненными полями получалось три длинных серых предложения подряд. Рядом с ними
   * заполненные значения («+7 982 726-68-53», «Новый Уренгой») терялись: карточку приходилось
   * ЧИТАТЬ, а её должны просматривать глазом сверху вниз.
   */
  missing?: string;
  /**
   * Что из этого следует. Показывается ТОЛЬКО когда значения нет.
   *
   * Отдельно от `note`, потому что `note` объясняет само значение («регион объекта: ЯНАО») и
   * при отсутствующем значении бессмысленно. Следствие пишется, только когда оно и правда
   * следствие: «на посты с требованием разряда не поставить» — да, «её вносят кадры при
   * оформлении» — нет, это порядок работы, а не факт об этом человеке.
   */
  missingNote?: ReactNode;
  /**
   * Значение ещё едет. Показывается скелетом, а не словами.
   *
   * Слово «загружаем…» серым текстом выглядит РОВНО так же, как «не заполнено» и «справочник
   * не приехал»: одинаковый кегль, одинаковый цвет, одинаковое место. Человек не отличает
   * «сейчас появится» от «данных нет» и уходит с экрана, решив, что поле пустое (поймано на
   * снимке карточки объекта 26.08.2026 — поле «Юрлицо»). Скелет отличается формой, и это
   * единственный признак загрузки, который не спутать с ответом.
   */
  pending?: boolean;
  /** Пояснение под значением: откуда оно взялось. */
  note?: ReactNode;
}

export function FieldRow({ label, value, missing, missingNote, pending, note }: FieldRowProps) {
  return (
    <div className='flex flex-col gap-0.5 border-b border-stroke-soft-200 py-2 last:border-b-0'>
      <dt className='text-paragraph-xs text-text-soft-400'>{label}</dt>
      <dd className='flex flex-col gap-0.5'>
        {pending === true ? (
          // Ширина в половину строки: полоса во всю ширину читается как настоящая длинная
          // строка и создаёт ложное ожидание объёма.
          <Skeleton className='h-5 w-1/2' />
        ) : value === null ? (
          <>
            <span className='text-paragraph-sm text-text-soft-400'>
              {missing ?? 'не заполнено'}
            </span>
            {missingNote !== undefined && (
              <span className='text-paragraph-xs text-text-sub-600'>{missingNote}</span>
            )}
          </>
        ) : (
          <span className='text-paragraph-sm text-text-strong-950'>{value}</span>
        )}
        {/* Пояснение при загрузке не показывается: оно объясняет ЗНАЧЕНИЕ, а значения ещё нет. */}
        {note !== undefined && pending !== true && (
          <span className='text-paragraph-xs text-text-sub-600'>{note}</span>
        )}
      </dd>
    </div>
  );
}

/**
 * Обёртка списка полей: карточка на белом, без лишних поверхностей.
 *
 * Заголовок необязателен, но нужен везде, где карточек больше одной. Без него человек видит
 * три одинаковые рамки подряд и должен сам догадаться, чем они отличаются: на карточке
 * сотрудника это были «кто он», «как оформлен» и «сколько должен» — три разных вопроса, между
 * которыми не было ни одного слова.
 */
export function FieldList({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <dl className='flex flex-col rounded-xl bg-bg-white-0 px-4 py-1 ring-1 ring-inset ring-stroke-soft-200'>
      {title !== undefined && (
        <dt className='border-b border-stroke-soft-200 py-2 text-label-sm text-text-strong-950'>
          {title}
        </dt>
      )}
      {children}
    </dl>
  );
}
